// Pause menu: settings fields and the keybind editor.

import {
  ACTIONS,
  MAX_BINDS_PER_ACTION,
  bind,
  defaultKeybinds,
  inputName,
  unbind,
  type Action,
} from '../input/keybinds';
import { FINISHES, type Finish } from '../render/finishes';
import {
  EDITOR_ACTIONS,
  MAX_EDITOR_BINDS,
  bindEditor,
  defaultEditorBinds,
  unbindEditor,
  type EditorAction,
} from '../editor/editorKeys';
import type { Settings } from '../storage/settings';

export type SettingsChange =
  | 'sens'
  | 'rules'
  | 'fov'
  | 'knife'
  | 'viewmodel'
  | 'volume'
  | 'fullscreen'
  | 'pixelRatio'
  | 'lowLatency'
  | 'binds'
  | 'editorBinds'
  | 'music';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

/** A list of rebindable actions: the game controls or the level builder controls. */
interface BindTable {
  el: HTMLElement;
  actions: readonly { id: string; label: string; group?: string }[];
  max: number;
  get(id: string): string[];
  bind(id: string, code: string, slot?: number): void;
  unbind(id: string, code: string): void;
  change: SettingsChange;
}

interface Capture {
  table: BindTable;
  action: string;
  slot?: number;
}

export class Menu {
  readonly el = $('menu');
  private capture: Capture | null = null;
  private readonly tables: BindTable[];

  /** Called after a setting changed and was saved. */
  onChange: (what: SettingsChange) => void = () => {};
  /** The "Random" finish button. */
  onRandomFinish: () => void = () => {};

  /** Show a finish picked elsewhere (the random key). */
  showFinish(f: Finish) {
    $<HTMLSelectElement>('sFinish').value = f;
  }

  constructor(s: Settings) {
    this.tables = [
      {
        el: $('binds'),
        actions: ACTIONS,
        max: MAX_BINDS_PER_ACTION,
        get: (id) => s.binds[id as Action],
        bind: (id, code, slot) => bind(s.binds, id as Action, code, slot),
        unbind: (id, code) => unbind(s.binds, id as Action, code),
        change: 'binds',
      },
      {
        el: $('editorBinds'),
        actions: EDITOR_ACTIONS,
        max: MAX_EDITOR_BINDS,
        get: (id) => s.editorBinds[id as EditorAction],
        bind: (id, code, slot) => bindEditor(s.editorBinds, id as EditorAction, code, slot),
        unbind: (id, code) => unbindEditor(s.editorBinds, id as EditorAction, code),
        change: 'editorBinds',
      },
    ];
    const sSens = $<HTMLInputElement>('sSens');
    const sAA = $<HTMLSelectElement>('sAA');
    const sAuto = $<HTMLInputElement>('sAuto');
    const sKnife = $<HTMLInputElement>('sKnife');
    const sFull = $<HTMLInputElement>('sFull');
    const sLowLat = $<HTMLInputElement>('sLowLat');
    const sFinish = $<HTMLSelectElement>('sFinish');
    const sVmFov = $<HTMLInputElement>('sVmFov');
    const sLeft = $<HTMLInputElement>('sLeft');
    const sCss = $<HTMLInputElement>('sCssModels');
    const sVol = $<HTMLInputElement>('sVol');
    for (const [id, f] of Object.entries(FINISHES)) sFinish.add(new Option(f.label, id));
    sFinish.value = s.finish;
    sVmFov.value = String(s.viewmodelFov);
    $('vmFovVal').textContent = String(s.viewmodelFov);
    sLeft.checked = s.leftHanded;
    sCss.checked = s.cssModels;
    sVol.value = String(s.volume);
    $('volVal').textContent = String(s.volume);
    $('sFinishRandom').addEventListener('click', () => this.onRandomFinish());
    sFinish.addEventListener('change', () => {
      s.finish = sFinish.value as Finish;
      this.onChange('viewmodel');
    });
    sVmFov.addEventListener('input', () => {
      s.viewmodelFov = parseInt(sVmFov.value, 10);
      $('vmFovVal').textContent = String(s.viewmodelFov);
      this.onChange('viewmodel');
    });
    sCss.addEventListener('change', () => {
      s.cssModels = sCss.checked;
      this.onChange('viewmodel');
    });
    sLeft.addEventListener('change', () => {
      s.leftHanded = sLeft.checked;
      this.onChange('viewmodel');
    });
    sVol.addEventListener('input', () => {
      s.volume = parseInt(sVol.value, 10);
      $('volVal').textContent = String(s.volume);
      this.onChange('volume');
    });
    const sMusic = $<HTMLInputElement>('sMusic');
    sMusic.value = String(s.musicVolume);
    $('musicVal').textContent = String(s.musicVolume);
    sMusic.addEventListener('input', () => {
      s.musicVolume = parseInt(sMusic.value, 10);
      $('musicVal').textContent = String(s.musicVolume);
      this.onChange('music');
    });
    const sFov = $<HTMLInputElement>('sFov');
    const fovVal = $('fovVal');
    const sPR = $<HTMLSelectElement>('sPR');

    sSens.value = String(s.sens);
    sAA.value = String(s.airaccel);
    sAuto.checked = s.autobhop;
    const sSlide = $<HTMLInputElement>('sSlide');
    sSlide.checked = s.slopeSlide;
    sKnife.checked = s.knife;
    sFull.checked = s.fullscreen;
    sLowLat.checked = s.lowLatency;
    sFov.value = String(s.fov);
    fovVal.textContent = String(s.fov);
    sPR.value = String(s.maxPixelRatio);
    if (sPR.value === '') sPR.value = '2';

    sSens.addEventListener('change', () => {
      const v = parseFloat(sSens.value);
      if (v > 0) {
        s.sens = v;
        this.onChange('sens');
      } else sSens.value = String(s.sens);
    });
    sAA.addEventListener('change', () => {
      s.airaccel = parseInt(sAA.value, 10);
      this.onChange('rules');
    });
    sAuto.addEventListener('change', () => {
      s.autobhop = sAuto.checked;
      this.onChange('rules');
    });
    sSlide.addEventListener('change', () => {
      s.slopeSlide = sSlide.checked;
      this.onChange('rules');
    });
    sFull.addEventListener('change', () => {
      s.fullscreen = sFull.checked;
      this.onChange('fullscreen');
    });
    sKnife.addEventListener('change', () => {
      s.knife = sKnife.checked;
      this.onChange('knife');
    });
    sFov.addEventListener('input', () => {
      s.fov = parseInt(sFov.value, 10);
      fovVal.textContent = String(s.fov);
      this.onChange('fov');
    });
    sLowLat.addEventListener('change', () => {
      s.lowLatency = sLowLat.checked;
      this.onChange('lowLatency');
    });
    sPR.addEventListener('change', () => {
      s.maxPixelRatio = parseFloat(sPR.value);
      this.onChange('pixelRatio');
    });

    $('bindsReset').addEventListener('click', () => {
      s.binds = defaultKeybinds();
      this.capture = null;
      this.renderBinds();
      this.onChange('binds');
    });
    $('editorBindsReset').addEventListener('click', () => {
      s.editorBinds = defaultEditorBinds();
      this.capture = null;
      this.renderBinds();
      this.onChange('editorBinds');
    });

    // Capture runs before the game's own handlers and swallows the event.
    window.addEventListener('keydown', (e) => this.onCaptureKey(e), true);
    window.addEventListener('mousedown', (e) => this.onCaptureMouse(e), true);
    window.addEventListener('wheel', (e) => this.onCaptureWheel(e), { capture: true, passive: false });

    this.renderBinds();
  }

  get capturing() {
    return this.capture !== null;
  }

  setVisible(v: boolean) {
    this.el.hidden = !v;
    if (!v && this.capture) {
      this.capture = null;
      this.renderBinds();
    }
  }

  private finishCapture(code: string | null) {
    const c = this.capture;
    if (!c) return;
    this.capture = null;
    if (code) {
      c.table.bind(c.action, code, c.slot);
      this.onChange(c.table.change);
    }
    this.renderBinds();
  }

  private onCaptureKey(e: KeyboardEvent) {
    if (!this.capture) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.finishCapture(e.code === 'Escape' ? null : e.code);
  }

  private onCaptureMouse(e: MouseEvent) {
    if (!this.capture) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.finishCapture('Mouse' + e.button);
  }

  private onCaptureWheel(e: WheelEvent) {
    if (!this.capture || e.deltaY === 0) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.finishCapture(e.deltaY < 0 ? 'WheelUp' : 'WheelDown');
  }

  private renderBinds() {
    for (const t of this.tables) this.renderTable(t);
  }

  private renderTable(t: BindTable) {
    const el = t.el;
    el.textContent = '';
    let group = '';
    for (const a of t.actions) {
      if (a.group && a.group !== group) {
        group = a.group;
        const h = document.createElement('span');
        h.className = 'bindGroup';
        h.textContent = group;
        el.append(h);
      }
      const label = document.createElement('span');
      label.className = 'act';
      label.textContent = a.label;
      const slots = document.createElement('span');
      slots.className = 'slots';
      const codes = t.get(a.id);
      const mine = (slot?: number) =>
        this.capture?.table === t && this.capture.action === a.id && this.capture.slot === slot;
      codes.forEach((code, i) => {
        const capturing = mine(i);
        const chip = document.createElement('span');
        chip.className = 'chip' + (capturing ? ' capture' : '');
        const key = document.createElement('button');
        key.type = 'button';
        key.textContent = capturing ? 'press a key…' : inputName(code);
        key.title = capturing ? 'Press a key, mouse button or scroll. Esc cancels.' : 'Change ' + a.label;
        key.addEventListener('click', () => this.startCapture({ table: t, action: a.id, slot: i }));
        chip.append(key);
        if (!capturing) {
          const x = document.createElement('button');
          x.type = 'button';
          x.className = 'x';
          x.textContent = '×';
          x.setAttribute('aria-label', 'Remove ' + inputName(code) + ' from ' + a.label);
          x.addEventListener('click', () => {
            t.unbind(a.id, code);
            this.renderBinds();
            this.onChange(t.change);
          });
          chip.append(x);
        }
        slots.append(chip);
      });
      const adding = mine(undefined);
      if (adding || codes.length < t.max) {
        const chip = document.createElement('span');
        chip.className = 'chip add' + (adding ? ' capture' : '');
        const add = document.createElement('button');
        add.type = 'button';
        add.textContent = adding ? 'press a key…' : codes.length ? '+' : 'unbound +';
        add.title = adding ? 'Press a key, mouse button or scroll. Esc cancels.' : 'Add a key for ' + a.label;
        add.addEventListener('click', () => this.startCapture({ table: t, action: a.id }));
        chip.append(add);
        slots.append(chip);
      }
      el.append(label, slots);
    }
  }

  private startCapture(c: Capture) {
    // The click that started capture has already fired its mousedown, so the next input is the new bind.
    this.capture = c;
    this.renderBinds();
  }
}
